import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert } from "react-daisyui";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import type { OnCompleteArgs } from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import shouldCollectBookings from "@/utils/shouldCollectBookings.ts";
import SpecialReasonForTrip from "$/_const/trip/SpecialReasonForTrip.ts";
import type { SpecialReasonForTrip as SpecialReasonForTripT } from "$/_const/trip/SpecialReasonForTrip.ts";
import WhoIsJoining, {
  allWhoIsJoining,
} from "$/_const/trip/WhoIsJoining.ts";
import type { WhoIsJoining as WhoIsJoiningT } from "$/_const/trip/WhoIsJoining.ts";
import type { CriticalRequirementsData } from "$/models/Dna.d.ts";
import type { TripData } from "$/models/Trip.model.ts";
import { tripCreateSchema } from "$/requests/TripCreateRequest.ts";

const MAX_CHILDREN = 6;
const MAX_CHILD_AGE = 17;
const MAX_CHILD_AGE_IN_MONTHS = 23;

const childAgeOptions = [
  ...Array.from({ length: MAX_CHILD_AGE_IN_MONTHS }, (_, index) => {
    const months = index + 1;

    return {
      label: `${months} ${months === 1 ? "month" : "months"}`,
      value: months / 12,
    };
  }),
  ...Array.from({ length: MAX_CHILD_AGE - 1 }, (_, index) => {
    const years = index + 2;

    return {
      label: `${years} years`,
      value: years,
    };
  }),
];

const reasonsPerWhoIsJoining: Record<
  WhoIsJoiningT,
  SpecialReasonForTripT[]
> = {
  [WhoIsJoining.SOLO]: [
    SpecialReasonForTrip.SOLO_ESCAPE,
    SpecialReasonForTrip.BIRTHDAY,
    SpecialReasonForTrip.BUCKET_LIST,
    SpecialReasonForTrip.JUST_BECAUSE,
  ],
  [WhoIsJoining.COUPLE]: [
    SpecialReasonForTrip.HONEYMOON,
    SpecialReasonForTrip.ANNIVERSARY,
    SpecialReasonForTrip.ROMANTIC,
    SpecialReasonForTrip.BIRTHDAY,
    SpecialReasonForTrip.BUCKET_LIST,
    SpecialReasonForTrip.JUST_BECAUSE,
  ],
  [WhoIsJoining.GROUP_OF_FRIENDS]: [
    SpecialReasonForTrip.FRIENDS_TRIP,
    SpecialReasonForTrip.BIRTHDAY,
    SpecialReasonForTrip.BUCKET_LIST,
    SpecialReasonForTrip.JUST_BECAUSE,
  ],
  [WhoIsJoining.FAMILY]: [
    SpecialReasonForTrip.FAMILY_TRIP,
    SpecialReasonForTrip.BIRTHDAY,
    SpecialReasonForTrip.BUCKET_LIST,
    SpecialReasonForTrip.JUST_BECAUSE,
  ],
};

const whoAndWhyFormSchema = tripCreateSchema
  .pick({
    who_is_joining: true,
    are_there_children: true,
  })
  .extend({
    number_of_adults: z.number().int().min(1),
    is_it_special_reason_for_trip:
      tripCreateSchema.shape.is_it_special_reason_for_trip.optional(),
    children: z
      .array(
        z.object({
          age: z
            .number()
            .min(1 / 12)
            .max(MAX_CHILD_AGE)
            .nullable(),
        }),
      )
      .max(MAX_CHILDREN),
    special_reason_custom: z.string().trim().max(250).nullable(),
    apply_saved_accessibility_requirements: z.boolean(),
    additional_accessibility_requirements: z
      .string()
      .trim()
      .max(1000)
      .nullable(),
  })
  .superRefine((data, context) => {
    if (data.who_is_joining === WhoIsJoining.FAMILY) {
      data.children.forEach((child, index) => {
        if (child.age == null) {
          context.addIssue({
            code: "custom",
            path: ["children", index, "age"],
            message: "Please select this child's age.",
          });
        }
      });
    }

    if (
      !data.is_it_special_reason_for_trip &&
      !data.special_reason_custom
    ) {
      context.addIssue({
        code: "custom",
        path: ["is_it_special_reason_for_trip"],
        message: "Please choose an occasion or add your own.",
      });
    }
  });

type WhoAndWhyFormValues = z.infer<typeof whoAndWhyFormSchema>;

export type WithWhoSchema = Omit<WhoAndWhyFormValues, "children"> & {
  children_ages: number[];
};

const getDefaultAdultCount = (
  whoIsJoining?: WithWhoSchema["who_is_joining"],
) => {
  switch (whoIsJoining) {
    case WhoIsJoining.COUPLE:
      return 2;
    case WhoIsJoining.GROUP_OF_FRIENDS:
      return 3;
    case WhoIsJoining.FAMILY:
      return 2;
    case WhoIsJoining.SOLO:
    default:
      return 1;
  }
};

type WithWhoProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

type ExtendedTripData = TripData &
  Partial<
    Pick<
      WithWhoSchema,
      | "number_of_adults"
      | "children_ages"
      | "special_reason_custom"
      | "apply_saved_accessibility_requirements"
      | "additional_accessibility_requirements"
    >
  >;

type ChoiceCardProps = {
  checked: boolean;
  label: string;
  name: string;
  onBlur: () => void;
  onChange: () => void;
  value: string;
};

function ChoiceCard({
  checked,
  label,
  name,
  onBlur,
  onChange,
  value,
}: ChoiceCardProps) {
  return (
    <label
      className={`relative flex min-h-[66px] cursor-pointer items-center rounded-[18px] border border-[#8A6D23] px-5 py-3 font-sans text-lg font-normal transition-colors focus-within:ring-2 focus-within:ring-primary/30 focus-within:ring-offset-2 focus-within:ring-offset-journi-theme-light sm:px-7 sm:text-xl ${
        checked
          ? "bg-[#E8D09A]/45 text-[#8A6D23]"
          : "bg-white text-[#191919] hover:bg-primary/5"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onBlur={onBlur}
        onChange={onChange}
        className="sr-only"
      />
      <span>{label}</span>
    </label>
  );
}

function WithWhoNavigation() {
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();

  return (
    <div
      className={`fixed inset-0 z-50 ${
        isMenuOpen
          ? "visible pointer-events-auto"
          : "invisible pointer-events-none"
      }`}
      aria-hidden={!isMenuOpen}
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      <TripLayout.NavigationPanel
        className={`absolute inset-y-0 left-0 transition-transform duration-300 ease-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      />
    </div>
  );
}

function WithWho(props: WithWhoProps) {
  return (
    <TripLayoutProvider>
      <WhoAndWhyContent {...props} />
    </TripLayoutProvider>
  );
}

function WhoAndWhyContent({
  onComplete,
  onCompleteWhenDirty,
}: WithWhoProps) {
  const { t } = useTranslation();
  const { user, supabaseClient } = useAuthContext();
  const {
    nextStep,
    setContextData,
    contextData,
    previousStep,
    goToStep,
    steps,
  } =
    useStepperContext<typeof allTripCreateSteps, TripData>();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [savedCriticalRequirements, setSavedCriticalRequirements] =
    useState<CriticalRequirementsData | null>(
      user?.dna?.critical_requirements ?? null,
    );

  const initialTripData = contextData as ExtendedTripData | null;
  const userCriticalRequirements =
    user?.dna?.critical_requirements ?? null;
  const savedAccessibilityRequirements =
    savedCriticalRequirements?.accessibility_requirements ?? [];
  const savedAccessibilityCustom =
    savedCriticalRequirements?.accessibility_requirements_custom?.trim();
  const hasSavedAccessibilityRequirements =
    savedAccessibilityRequirements.length > 0 || !!savedAccessibilityCustom;

  const savedAccessibilityLabels = useMemo(
    () => [
      ...savedAccessibilityRequirements.map((requirement) =>
        t(
          [
            `travelDnaPage.accessibilityRequirements.${requirement}`,
            `travelDnaPage.accessAndSupport.${requirement}`,
          ],
          {
            defaultValue: missingTranslationToReadable(requirement),
          },
        ),
      ),
      ...(savedAccessibilityCustom ? [savedAccessibilityCustom] : []),
    ],
    [savedAccessibilityCustom, savedAccessibilityRequirements, t],
  );

  useEffect(() => {
    let isCurrent = true;

    if (!user?.id) {
      setSavedCriticalRequirements(null);
      return () => {
        isCurrent = false;
      };
    }

    void (async () => {
      const { data, error: dnaError } = await supabaseClient
        .from("dna")
        .select("critical_requirements")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!isCurrent) return;

      if (dnaError) {
        console.error(
          "Error loading saved accessibility requirements:",
          dnaError,
        );
        setSavedCriticalRequirements(userCriticalRequirements);
        return;
      }

      const dna = data as {
        critical_requirements?: CriticalRequirementsData | null;
      } | null;

      const databaseRequirements = dna?.critical_requirements;

      setSavedCriticalRequirements((previousRequirements) => ({
        ...previousRequirements,
        ...userCriticalRequirements,
        ...databaseRequirements,
        accessibility_requirements:
          databaseRequirements?.accessibility_requirements ??
          userCriticalRequirements?.accessibility_requirements ??
          previousRequirements?.accessibility_requirements ??
          [],
        accessibility_requirements_custom:
          databaseRequirements?.accessibility_requirements_custom ??
          userCriticalRequirements?.accessibility_requirements_custom ??
          previousRequirements?.accessibility_requirements_custom ??
          null,
      }));
    })();

    return () => {
      isCurrent = false;
    };
  }, [supabaseClient, user?.id, userCriticalRequirements]);

  const defaultChildren = useMemo(() => {
    if (initialTripData?.who_is_joining !== WhoIsJoining.FAMILY) return [];

    if (initialTripData.children_ages?.length) {
      return initialTripData.children_ages
        .slice(0, MAX_CHILDREN)
        .map((age) => ({ age }));
    }

    return [];
  }, [initialTripData]);

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm<WhoAndWhyFormValues>({
    resolver: zodResolver(whoAndWhyFormSchema),
    defaultValues: {
      who_is_joining: initialTripData?.who_is_joining,
      are_there_children:
        initialTripData?.who_is_joining === WhoIsJoining.FAMILY &&
        defaultChildren.length > 0,
      number_of_adults:
        initialTripData?.who_is_joining === WhoIsJoining.SOLO ||
        initialTripData?.who_is_joining === WhoIsJoining.COUPLE
          ? getDefaultAdultCount(initialTripData.who_is_joining)
          : initialTripData?.number_of_adults ??
            getDefaultAdultCount(initialTripData?.who_is_joining),
      children: defaultChildren,
      is_it_special_reason_for_trip:
        initialTripData?.who_is_joining &&
        initialTripData?.is_it_special_reason_for_trip &&
        reasonsPerWhoIsJoining[
          initialTripData.who_is_joining
        ].includes(initialTripData.is_it_special_reason_for_trip)
          ? initialTripData.is_it_special_reason_for_trip
          : undefined,
      special_reason_custom:
        initialTripData?.special_reason_custom ?? null,
      apply_saved_accessibility_requirements:
        initialTripData?.apply_saved_accessibility_requirements ??
        hasSavedAccessibilityRequirements,
      additional_accessibility_requirements:
        initialTripData?.additional_accessibility_requirements ?? null,
    },
  });

  const { fields: children, replace: replaceChildren } = useFieldArray({
    control,
    name: "children",
  });

  const selectedWho = watch("who_is_joining");
  const isFamilySelected = selectedWho === WhoIsJoining.FAMILY;
  const occasionOptions = selectedWho
    ? reasonsPerWhoIsJoining[selectedWho]
    : [];

  useEffect(() => {
    if (
      hasSavedAccessibilityRequirements &&
      initialTripData?.apply_saved_accessibility_requirements == null
    ) {
      setValue("apply_saved_accessibility_requirements", true, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [
    hasSavedAccessibilityRequirements,
    initialTripData?.apply_saved_accessibility_requirements,
    setValue,
  ]);

  const setChildrenCount = (nextCount: number) => {
    const count = Math.min(MAX_CHILDREN, Math.max(0, nextCount || 0));
    const currentChildren = getValues("children");

    replaceChildren(
      Array.from({ length: count }, (_, index) => ({
        age: currentChildren[index]?.age ?? null,
      })),
    );
    setValue("are_there_children", count > 0, {
      shouldDirty: true,
    });
  };

  const onSubmit = (formData: WhoAndWhyFormValues) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);

        const {
          children: submittedChildren,
          ...submittedFields
        } = formData;
        const data: WithWhoSchema = {
          ...submittedFields,
          number_of_adults:
            formData.who_is_joining === WhoIsJoining.SOLO ||
            formData.who_is_joining === WhoIsJoining.COUPLE
              ? getDefaultAdultCount(formData.who_is_joining)
              : formData.number_of_adults,
          are_there_children:
            formData.who_is_joining === WhoIsJoining.FAMILY &&
            submittedChildren.length > 0,
          children_ages:
            formData.who_is_joining === WhoIsJoining.FAMILY
              ? submittedChildren
                  .map((child) => child.age)
                  .filter((age): age is number => age != null)
              : [],
          special_reason_custom:
            formData.special_reason_custom?.trim() || null,
          apply_saved_accessibility_requirements:
            hasSavedAccessibilityRequirements
              ? formData.apply_saved_accessibility_requirements
              : false,
          additional_accessibility_requirements:
            formData.additional_accessibility_requirements?.trim() || null,
        };
        const nextContext = {
          ...contextData,
          ...data,
        } as TripData;
        const args: OnCompleteArgs = {
          step: TripCreateStep.WITH_WHO,
          data,
          context: nextContext,
        };

        if (isDirty) {
          setContextData(() => nextContext);
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);

        if (
          !shouldCollectBookings(nextContext) &&
          steps.includes(TripCreateStep.MORE_INFO)
        ) {
          goToStep(TripCreateStep.MORE_INFO);
        } else {
          nextStep();
        }
      } catch (caughtError) {
        const submitError =
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError));

        setError(submitError);
        console.error(caughtError);
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <>
  <WithWhoNavigation />

  <div
  className="min-h-[100dvh] max-w-none overflow-x-hidden bg-journi-theme-light text-[#191919]"
  style={{
    position: "relative",
    left: "50%",
    width: "100vw",
    minWidth: "100vw",
    maxWidth: "none",
    transform: "translateX(-50%)",
  }}
>
  <header className="flex w-full items-center justify-between gap-6 border-b border-primary/60 px-5 py-5 sm:px-8 md:px-14 lg:px-20">
    <div className="flex items-center gap-4">
      <TripLayout.OpenMenuButton color="primary" />

      <span className="font-feature text-3xl font-bold">
        {t("brand.name")}
      </span>
    </div>
  </header>

  <main className="w-full px-5 py-8 sm:px-8 md:px-[5vw] md:py-10">

        {error ? (
          <Alert status="error" className="mt-6">
            {error.message}
          </Alert>
        ) : null}

        <form
          className="mx-auto flex w-full max-w-[920px] flex-col"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <p className="font-sans text-sm font-semibold uppercase tracking-wide text-primary">
            {t("tripCreateForm.whoAndWhy.eyebrow", "Who & why")}
          </p>
          <h1 className="mt-1 font-feature text-3xl font-normal leading-tight md:text-4xl">
            {t("tripCreateForm.whoAndWhy.titleStart", "Who's coming, and")}{" "}
            <span className="italic text-primary">
              {t(
                "tripCreateForm.whoAndWhy.titleHighlight",
                "what's the occasion?",
              )}
            </span>
          </h1>

          <section className="mt-6">
            <h2 className="mb-4 font-sans text-base font-normal text-text-secondary md:text-lg">
              {t(
                "tripCreateForm.whoAndWhy.whoQuestion",
                "Who's coming along?",
              )}
            </h2>

            <Controller
              control={control}
              name="who_is_joining"
              render={({ field: { name, onBlur, onChange, value } }) => (
                <fieldset>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-14">
                    {allWhoIsJoining.map((option) => (
                      <ChoiceCard
                        key={option}
                        checked={value === option}
                        label={t(
                          `enum_translations.WhoIsJoining.${option}`,
                        )}
                        name={name}
                        value={option}
                        onBlur={onBlur}
                        onChange={() => {
                          const currentOccasion = getValues(
                            "is_it_special_reason_for_trip",
                          );
                          onChange(option);
                          const isFamily = option === WhoIsJoining.FAMILY;

                          if (
                            currentOccasion &&
                            !reasonsPerWhoIsJoining[option].includes(
                              currentOccasion,
                            )
                          ) {
                            setValue(
                              "is_it_special_reason_for_trip",
                              undefined,
                              { shouldDirty: true },
                            );
                          }

                          setValue(
                            "number_of_adults",
                            getDefaultAdultCount(option),
                            { shouldDirty: true },
                          );
                          setValue(
                            "are_there_children",
                            isFamily && children.length > 0,
                            {
                              shouldDirty: true,
                            },
                          );

                          if (!isFamily) {
                            replaceChildren([]);
                          }
                        }}
                      />
                    ))}
                  </div>

                  {errors.who_is_joining?.message ? (
                    <p className="mt-2 text-sm text-error" role="alert">
                      {errors.who_is_joining.message}
                    </p>
                  ) : null}
                </fieldset>
              )}
            />
          </section>

          {selectedWho === WhoIsJoining.FAMILY ||
          selectedWho === WhoIsJoining.GROUP_OF_FRIENDS ? (
            <section className="mt-7">
              <Controller
                control={control}
                name="number_of_adults"
                render={({ field, fieldState }) => (
                  <label className="block">
                    <span className="mb-3 block font-sans text-base font-normal text-text-secondary md:text-lg">
                      {t(
                        "tripCreateForm.whoAndWhy.adultCount",
                        "How many adults are joining?",
                      )}
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={field.value}
                      disabled={isPending}
                      className="h-[60px] w-full rounded-[14px] border border-[#8A6D23] bg-white px-5 font-sans text-base font-normal outline-none focus:ring-2 focus:ring-primary/20"
                      onBlur={field.onBlur}
                      onChange={(event) =>
                        field.onChange(Number(event.target.value))
                      }
                    />
                    {fieldState.error?.message ? (
                      <span className="mt-2 block text-sm text-error">
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </label>
                )}
              />
            </section>
          ) : null}

          {isFamilySelected ? (
            <section className="mt-7">
              <label
                htmlFor="children-count"
                className="mb-3 block font-sans text-base font-normal text-text-secondary md:text-lg"
              >
                {t(
                  "tripCreateForm.whoAndWhy.childrenCount",
                  "How many children are travelling?",
                )}
              </label>
              <input
                id="children-count"
                type="number"
                min={0}
                max={MAX_CHILDREN}
                value={children.length}
                disabled={isPending}
                className="h-[60px] w-full rounded-[14px] border border-[#8A6D23] bg-white px-5 font-sans text-base font-normal outline-none focus:ring-2 focus:ring-primary/20"
                onChange={(event) =>
                  setChildrenCount(Number(event.target.value))
                }
              />

              <div
                className={`mt-5 grid grid-cols-1 gap-5 ${
                  children.length > 1 ? "sm:grid-cols-2 sm:gap-x-14" : ""
                }`}
              >
                {children.map((child, index) => (
                  <Controller
                    key={child.id}
                    control={control}
                    name={`children.${index}.age` as const}
                    render={({ field, fieldState }) => (
                      <label className="block">
                        <span className="mb-3 block font-sans text-base font-normal text-text-secondary md:text-lg">
                          {t(
                            "tripCreateForm.whoAndWhy.childAge",
                            "Age of child {{count}}",
                            { count: index + 1 },
                          )}
                        </span>
                        <div className="relative">
                          <select
                            value={field.value ?? ""}
                            disabled={isPending}
                            className={`h-[60px] w-full cursor-pointer appearance-none rounded-[14px] border border-[#8A6D23] bg-white px-5 pr-14 font-sans text-base font-normal outline-none transition-colors hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                              field.value == null
                                ? "text-text-secondary"
                                : "text-[#191919]"
                            }`}
                            onBlur={field.onBlur}
                            onChange={(event) =>
                              field.onChange(
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                              )
                            }
                          >
                            <option value="" disabled>
                              Select age
                            </option>
                            {childAgeOptions.map((option) => (
                              <option key={option.label} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8A6D23]"
                          />
                        </div>
                        {fieldState.error?.message ? (
                          <span className="mt-2 block text-sm text-error">
                            {fieldState.error.message}
                          </span>
                        ) : null}
                      </label>
                    )}
                  />
                ))}
              </div>

              {typeof errors.children?.message === "string" ? (
                <p className="mt-2 text-sm text-error" role="alert">
                  {errors.children.message}
                </p>
              ) : null}
            </section>
          ) : null}

          {selectedWho ? (
            <section className="mt-7">
            <h2 className="mb-4 font-sans text-base font-normal text-text-secondary md:text-lg">
              {t(
                "tripCreateForm.whoAndWhy.occasionQuestion",
                "Is there a reason for this one?",
              )}
            </h2>

            <Controller
              control={control}
              name="is_it_special_reason_for_trip"
              render={({ field: { name, onBlur, onChange, value } }) => (
                <fieldset>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-14">
                    {occasionOptions.map((option) => (
                      <ChoiceCard
                        key={option}
                        checked={value === option}
                        label={
                          option === SpecialReasonForTrip.JUST_BECAUSE
                            ? t(
                                "tripCreateForm.whoAndWhy.justBecause",
                                "Just because – no reason needed",
                              )
                            : t(
                                `enum_translations.SpecialReasonForTrip.${option}`,
                              )
                        }
                        name={name}
                        value={option}
                        onBlur={onBlur}
                        onChange={() => {
                          onChange(option);
                          setValue("special_reason_custom", null, {
                            shouldDirty: true,
                          });
                        }}
                      />
                    ))}
                  </div>

                  {errors.is_it_special_reason_for_trip?.message ? (
                    <p className="mt-2 text-sm text-error" role="alert">
                      {errors.is_it_special_reason_for_trip.message}
                    </p>
                  ) : null}
                </fieldset>
              )}
            />

            <Controller
              control={control}
              name="special_reason_custom"
              render={({ field, fieldState }) => (
                <div className="mt-5">
                  <input
                    type="text"
                    maxLength={250}
                    value={field.value ?? ""}
                    disabled={isPending}
                    placeholder={t(
                      "tripCreateForm.whoAndWhy.otherOccasionPlaceholder",
                      "Something else...",
                    )}
                    className="h-[60px] w-full rounded-[14px] border border-[#8A6D23] bg-white px-5 font-sans text-base font-normal outline-none placeholder:font placeholder:text-text-secondary focus:ring-2 focus:ring-primary/20"
                    onBlur={field.onBlur}
                    onChange={(event) => {
                      field.onChange(event.target.value);

                      if (event.target.value.trim()) {
                        setValue(
                          "is_it_special_reason_for_trip",
                          undefined,
                          { shouldDirty: true },
                        );
                      }
                    }}
                  />
                  {fieldState.error?.message ? (
                    <p className="mt-2 text-sm text-error" role="alert">
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </div>
              )}
            />
            </section>
          ) : null}

          <section className="mt-8">
            <h2 className="font-feature text-3xl font-bold">
              {t(
                "tripCreateForm.whoAndWhy.accessSupport",
                "Access & support",
              )}
            </h2>

            {hasSavedAccessibilityRequirements ? (
              <>
                <div className="mt-4 rounded-[14px] border border-[#8A6D23] bg-white p-5">
                  <p className="font-sans text-sm font-normal text-[#8A6D23] md:text-base">
                    {t(
                      "tripCreateForm.whoAndWhy.savedRequirements",
                      "Your Travel DNA already has these saved requirements:",
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {savedAccessibilityLabels.map((label) => (
                      <span
                        key={label}
                        className="rounded-full border border-[#8A6D23] bg-[#E8D09A]/35 px-3 py-1 font-sans text-xs font-normal text-[#8A6D23]"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                <Controller
                  control={control}
                  name="apply_saved_accessibility_requirements"
                  render={({ field }) => (
                    <fieldset className="mt-5">
                      <legend className="font-sans text-base font-normal text-text-secondary md:text-lg">
                        {t(
                          "tripCreateForm.whoAndWhy.requirementsApply",
                          "Do these requirements apply to this trip?",
                        )}
                      </legend>
                      <div className="mt-4 flex flex-wrap gap-4 sm:gap-8">
                        {[
                          {
                            value: true,
                            label: t(
                              "tripCreateForm.whoAndWhy.requirementsYes",
                              "Yes – plan around them",
                            ),
                          },
                          {
                            value: false,
                            label: t(
                              "tripCreateForm.whoAndWhy.requirementsNo",
                              "No – not for this trip",
                            ),
                          },
                        ].map((option) => (
                          <label
                            key={String(option.value)}
                            className={`cursor-pointer rounded-full border border-[#191919] px-5 py-2 font-sans text-sm font-semibold transition-colors ${
                              field.value === option.value
                                ? "bg-[#191919] text-white"
                                : "bg-white text-[#191919]"
                            }`}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              checked={field.value === option.value}
                              onChange={() => field.onChange(option.value)}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  )}
                />
              </>
            ) : null}

            <Controller
              control={control}
              name="additional_accessibility_requirements"
              render={({ field, fieldState }) => (
                <label className="mt-8 block">
                  <span className="flex flex-wrap items-center gap-3 font-sans text-base font-normal text-text-secondary md:text-lg">
                    {selectedWho === WhoIsJoining.SOLO
                      ? t(
                          "tripCreateForm.whoAndWhy.additionalSupportSolo",
                          "Are there any additional access or support needs specific to this trip?",
                        )
                      : t(
                          "tripCreateForm.whoAndWhy.additionalSupport",
                          "Does anyone travelling with you have any additional access or support needs?",
                        )}
                    <span className="rounded-full border border-[#8A6D23] px-2 py-0.5 text-[10px] uppercase text-[#8A6D23]">
                      {t("label.optional", "Optional")}
                    </span>
                  </span>
                  <textarea
                    rows={5}
                    maxLength={1000}
                    value={field.value ?? ""}
                    disabled={isPending}
                    placeholder={t(
                      "tripCreateForm.whoAndWhy.additionalSupportPlaceholder",
                      "e.g. My partner uses a wheelchair and requires step-free access...",
                    )}
                    className="mt-3 min-h-[150px] w-full resize-y rounded-[14px] border border-[#8A6D23] bg-white p-4 font-sans text-base font-normal outline-none placeholder:font placeholder:text-text-secondary focus:ring-2 focus:ring-primary/20"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                  {fieldState.error?.message ? (
                    <span className="mt-2 block text-sm text-error">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </label>
              )}
            />
          </section>

          <div className="mt-9 flex flex-col items-center gap-3 pb-2">
            <LoadingButton
              loading={isPending}
              type="submit"
              color="primary"
              className="journi-primary-button w-full sm:w-[250px]"
              endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
            >
              {t("button.continue")}
            </LoadingButton>

            <button
              type="button"
              className="min-h-11 px-6 font-sans text-base font-semibold text-[#191919] hover:text-primary"
              onClick={previousStep}
            >
              {t("button.goBack")}
            </button>
          </div>
                </form>
      </main>
    </div>
  </>
);
}

export default WithWho;
